import { MongoClient, InsertWriteOpResult, ObjectId, DeleteWriteOpResultObject, FilterQuery, UpdateWriteOpResult, WriteOpResult } from 'mongodb';
import { BaseService } from '../../base';
import { typeNamePropertyName, addon, BaseAddon, extend, IDataService, DataProcessResult, DataServiceMethods } from 'pao-aop';

/** 时间匹配正则表达式 */
const DATE_REGEXP = /__Date__/;
/** 时间类型自定义 */
const DATE_TYPE_SELF = '__Date__';
/** 时间类型判断条件 */
const DATE_TYPE_STRING = '[object Date]';

/** 连接配置 */
export type ConnectionOption = {
    ip: string,
    port: string,
    database: string
};

/** 逻辑运算符 */
export enum LogicKeys {
    AND,
    OR
}

/** 数据过滤器接口 */
export interface IMongoDataFilter {
    /**
     * 获取条件
     * @param paramValues 参数 
     */
    getCondition?(paramValues: {}): any;
}

/**
 * 名称:过滤器
 * @description 用于描述Mongo数据操作的过滤器
 * @author huyl
 */
@addon('BaseMongoDataFilter', 'Mongo过滤器基类', '用于描述Mongo数据操作的过滤器')
export class BaseMongoDataFilter extends BaseAddon implements IMongoDataFilter {
    /** 
     * 过滤器
     * @param name 名称
     * @param childrenFilters 子过滤器
     */
    constructor(public name?: string, public childrenFilters?: IMongoDataFilter[]) {
        super();
    }
    /** 获取提条件 */
    getCondition?(paramValues: {}): any {
        let and: any = { $and: [] }, or: any = { $or: [] };
        for (let index = 0, len = this.childrenFilters!.length;
            index < len; index++) {
            let childrenFilter = this.childrenFilters![index];
            let logicKey = this.getLogicKey();
            let filterJson = childrenFilter.getCondition(paramValues);
            if (!filterJson) {
                continue;
            }
            // AND 逻辑
            if (!logicKey || logicKey === LogicKeys.AND) {
                and.$and.push(filterJson);
            }
            // OR 逻辑
            if (logicKey === LogicKeys.OR) {
                or.$or.push(filterJson);
            }
        }
        if (or.$or.length > 0) {
            return extend(and, or, true);
        } else {
            return and;
        }
    }
    /** 获取逻辑运算符 */
    protected getLogicKey?(): any {
        // 默认AND逻辑
        return LogicKeys.AND;
    }
}

/**
 * 名称:And逻辑过滤器
 * @description 用于描述 Mongo 数据操作 AND 逻辑过滤器
 * @author huyl
 */
@addon('AndMongoDataFilter', 'And逻辑过滤器', '用于描述 Mongo 数据操作 AND 逻辑过滤器')
export class AndMongoDataFilter extends BaseMongoDataFilter {
    /** 
     * And逻辑过滤器
     * @param name 名称
     * @param childrenFilters 子过滤器
     */
    constructor(public name?: string, public childrenFilters?: IMongoDataFilter[]) {
        super(name, childrenFilters);
    }
    protected getLogicKey(): any {
        return LogicKeys.AND;
    }
}

/**
 * 名称:Or逻辑过滤器
 * @description 用于描述 Mongo 数据操作 OR 逻辑过滤器
 * @author huyl
 */
@addon('OrMongoDataFilter', 'Or逻辑过滤器', '用于描述 Mongo 数据操作 OR 逻辑过滤器')
export class OrMongoDataFilter extends BaseMongoDataFilter {

    /** 
     * And逻辑过滤器
     * @param name 名称
     * @param childrenFilters 子过滤器
     */
    constructor(public name?: string, public childrenFilters?: IMongoDataFilter[]) {
        super(name, childrenFilters);
    }
    protected getLogicKey(): any {
        return LogicKeys.OR;
    }
}

/**
 * 数据类型
 */
export enum DataType {
    Date,
    String,
    Number
}

/**
 * 名称:字符串条件语句
 * @description 用于Mongo数据操作条件的最基础的语句
 * @author huyl
 */
@addon('StringMongoDataFilter', '字符串条件语句', '用于Mongo数据操作条件的最基础的语句')
export class StringMongoDataFilter extends BaseMongoDataFilter {
    /** 数据类型 */
    dataType?: DataType;
    /** 
     * 字符串条件语句 
     * @param name 名称
     * @param key 字段名称
     * @param condition 条件
     * @param dataType 数据类型
     */
    constructor(
        name?: string,
        public key?: string,
        public condition?: string,
        dataType?: DataType) {
        super(name);
        if (typeof dataType === 'undefined') {
            this.dataType = DataType.String;
        }
        this.dataType = dataType;
    }

    getCondition?(paramValues: {}) {
        if (!paramValues || !paramValues[this.key!]) {
            return undefined;
        }
        let value = paramValues[this.key!];
        let filterString = this.format(this.condition!, value);
        return JSON.parse(filterString, (key, value) => {
            let reg = new RegExp(DATE_REGEXP);
            if (reg.test(value)) {
                return new Date(value.replace(reg, ''));
            }
            return value;
        });
    }

    /** 
     * 字符串格式化 
     * @description 需要处理特殊格式，如时间等
     */
    format?(origin: string, ...args: any[]) {
        //如果是时间
        return origin.replace(/\{([1-9]\d*|0)\}/g, (s, i) => {
            if (this.dataType === DataType.Date) {
                let temp = [];
                temp.push(DATE_TYPE_SELF, args[i].toUTCString());
                return `"${temp.join('')}"`;
            }
            return args[i];
        });
    }
}

/**
 * 名称:集合
 * @description 用于描述Mongo数据集合的对象
 * @author huyl
 */
@addon('BaseMongoCollection', '集合', '用于描述Mongo数据集合的对象')
export class BaseMongoCollection extends BaseAddon {
    /** 
     * 命令
     * @param {string} name 名称
     * @param {string} collection 集合名称
     * @param {Object} filters 过滤器
     */
    constructor(
        public name?: string,
        public collection?: string,
        public primaryKeys?: string[],
        public filters?: {
            [index: string]: {
                name: string;
                filter?: BaseMongoDataFilter
            }
        }) {
        super();
    }

    /** 
     * 获取条件 
     */
    getCondition?(filterName: string, paramValues?: {}) {
        if (!paramValues || !this.filters || Object.keys(paramValues).length === 0) {
            return {};
        }
        return this.filters[filterName].filter.getCondition(paramValues);
    }
}

/**
 * 名称:Mongo DB 连接池
 * @description 用于操作Mongo DB连接信息的对象
 * @author huyl
 */
@addon('MongoConnectionPool', 'Mongo DB 连接池', '用于操作Mongo DB连接信息的对象')
export class MongoConnectionPool extends BaseAddon {
    /** Mongo DB 连接池 */
    constructor(protected conn: ConnectionOption | string, protected database?: string) {
        super();
    }
    /** 连接信息 */
    protected get uri(): string | undefined {
        if (typeof this.conn === 'object') {
            return `mongodb://${this.conn.ip}:${this.conn.port}${this.conn.database ? `/${this.conn.database}` : null}`;
        }

        if (typeof this.conn === 'string') {
            return this.conn;
        }

        return undefined;
    }
    /** 创建连接 */
    public get connection() {
        return MongoClient.connect(this.uri!, { useNewUrlParser: true });
    }
    /** 获取数据库名称 */
    public get dbName() {
        return typeof this.conn === 'string'
            ? this.database
            : typeof this.conn === 'object'
                ? this.conn.database
                : undefined;
    }
}

/**
 * 名称:Mongo DB 数据存储
 * @description 完成Mongo DB主要增删查改等操作
 * @author huyl
 */
@addon('MongoStorage', 'Mongo DB 数据存储', '完成Mongo DB主要增删查改等操作')
export class MongoStorage extends BaseAddon {
    /** Mongo 数据库每个文本的_id */
    private obj_id_key?= '_id';

    /** Mongo DB 数据存储 */
    constructor(protected pool: MongoConnectionPool) {
        super();
    }
    /** 
     * 查询
     * @param {string} collection 集合名称
     * @param {{}} where 条件
     * @param {{}} option 统计配置
     * @returns {Promise<any[]>} 结果
     */
    async select?(collection: string, where?: FilterQuery<any>, option?: { skip: number, limit: number }): Promise<Object[]> {
        let client = await this.pool.connection;

        const skip = option && option.skip ? option.skip : 0,
            limit = option && option.limit ? option.limit : 0,
            condition = where ? where : {};
        let db = client.db(this.pool.dbName);
        let col = db.collection(collection);
        try {
            let doc = await col
                .find(condition)    // 条件筛选
                .skip(skip)         // 跳过行数，默认0
                .limit(limit)       // 返回行数，默认0，查找全部
                .toArray();

            const ret = doc.map(obj => {
                if (this.obj_id_key! in obj) {
                    let _id: ObjectId = obj[this.obj_id_key!];
                    obj[this.obj_id_key!] = _id.toHexString && _id.toHexString();
                }
                return obj;
            });
            return ret;
        } finally {
            client.close(); // 关闭客户端连接
        }
    }
    /**
     * 更新
     * @param {string} collection 集合名称
     * @param {any[]} document 文档集合
     * @returns {Promise<WriteOpResult>} 结果
     */
    async update?(collection: string, documents?: Object[]): Promise<number> {
        let client = await this.pool.connection;
        let db = client.db(this.pool.dbName);
        try {
            let updateCount = 0;
            for (let doc of documents) {
                // _id为字符串时，需要转换为ObjectID才能匹配原数据
                if (this.obj_id_key in doc &&
                    typeof doc[this.obj_id_key] === 'string') {
                    doc[this.obj_id_key] = new ObjectId(doc[this.obj_id_key]);
                }
                let writeOpResult = await db.collection(collection).replaceOne({}, doc!);
                updateCount += writeOpResult.result.n;
            }
            return updateCount;
        } finally {
            client.close();
        }
    }

    /**
     * 添加
     * @param collection 集合名称
     * @param documents 文档列表
     */
    async insert?(collection: string, documents: Object[]): Promise<InsertWriteOpResult> {
        let client = await this.pool.connection;
        let db = client.db(this.pool.dbName);
        try {
            let col = await db.createCollection(collection);
            let value = await col.insertMany(documents);
            return value;
        } finally {
            client.close();
        }
    }
    /**
     * 删除
     * @param {string} collection 集合名称
     * @param {string[]} primaryKeys 主键列表
     * @param {Object[]} objects 对象
     * @returns {Promise<any>} 结果
     */
    async delete?(collection: string, primaryKeys: string[], objects?: Object[]): Promise<number> {
        let client = await this.pool.connection;
        let db = client.db(this.pool.dbName);
        let deleteCount = 0;
        try {
            if (objects) {
                for (let object of objects) {
                    let where = this.getFilterByObject(primaryKeys, object);
                    let value = await db.collection(collection).deleteOne(where);
                    deleteCount += value.deletedCount;
                }
            }
            return deleteCount;
        } finally {
            client.close();
        }
    }
    /**
     * 获取集合文档数量
     * @param collection 集合名称
     * @param where 条件
     */
    async count?(collection: string, where?: {}): Promise<number> {
        let client = await this.pool.connection;
        let db = client.db(this.pool.dbName);
        try {
            const condition = where ? where : {};
            let value = await db.collection(collection).count(condition);
            return value;
        } finally {
            client.close();
        }
    }
    /**
     * 
     * @param primaryKeys 主键关键字
     * @param object 
     */
    private getFilterByObject?= (primaryKeys: string[], object: Object): FilterQuery<any> => {
        // TODO: PAO, 此处需要从object中通过command的主键来获取过滤器
        if (!primaryKeys || primaryKeys.length === 0) {
            // 如果不存在主键则返回MongoDB默认主键
            // 确保_id为ObjectId类型
            return { [this.obj_id_key]: new ObjectId(object[this.obj_id_key]) };
        }
        let where = {};
        for (let key of primaryKeys) {
            // 当主键存在object时添加
            if (key in object) {
                where[key] = object[key];
            }
        }
        return where;
    }
}

/**
 * 名称:Mongo 数据查询服务
 * @description 提供Mongo数据库查询的服务
 * @author huyl
 */
@addon('MongoQueryService', 'Mongo 数据服务', '提供Mongo数据库查询的服务')
export class MongoQueryService extends BaseService implements IDataService {
    /** 集合列表 */
    collectionList?: { [index: string]: BaseMongoCollection };
    /** 
     * Mongo 数据服务
     * @param storage 数据存储
     */
    constructor(public storage?: MongoStorage) {
        super();
        this.collectionList = {};
    }
    /**
     * 检查合法性
     * @protected
     * @param {string} commandString 命令ID
     * @returns {BaseMongoCollection} 
     */
    protected checkValidity?(commandString: string): BaseMongoCollection {
        if (!this.storage) { throw new Error('当前服务没有数据库存储'); }
        if (!commandString) { throw new Error('查询的命令不能为空'); }
        // 根据命令ID获取命令
        let cmd = this.collectionList![commandString];
        if (!cmd) { throw new Error(`当前服务没有[${commandString}]的命令`); }
        if (!cmd.collection) { throw new Error(`该命令不存在集合名称`); }
        return cmd;
    }
    /**
     * 获取命令和过滤器名称
     * @param commandID 命令ID
     */
    private getCommandAndFilterName?= (commandID: string) => {
        let commands = commandID.split(".", 2);
        let cmd = this.checkValidity!(commands[0]);
        let filterName = commands[1];
        return { command: cmd, filterName: filterName, primaryKeys: cmd.primaryKeys };
    }
    /**
     * 获取支持的方法
     */
    async getSupportMethods?(): Promise<DataServiceMethods[]> {
        return ['query', 'update', 'updateMany', 'insert', 'delete', 'deleteMany', 'count'];
    }

    /**
     * 查询
     * @param {string} commandID 命令ID
     * @param {{}} paramValues 参数
     * @param {number} startIndex 开始索引
     * @param {number} maxCount 最大行数,默认0为查询全部数据
     */
    async query?(commandID: string, paramValues: {}, startIndex: number = 0, maxCount: number = 0): Promise<any[]> {
        // 检查有效性并获取命令
        const { command, filterName } = this.getCommandAndFilterName(commandID);
        // 获取条件
        let where = command.getCondition(filterName, paramValues);
        // 通过存储执行查询
        return await this.storage!.select!(command.collection!, where, { skip: startIndex, limit: maxCount });
    }

    /**
     * 插入
     * @param commandID 命令名称
     * @param objects 对象 
     */
    async insert?(commandID: string, objects: Object[]): Promise<DataProcessResult> {
        // 检查有效性并获取命令
        const { command } = this.getCommandAndFilterName(commandID);
        let insertObjects = objects.map(obj => {
            if (typeNamePropertyName in obj) {
                delete obj[typeNamePropertyName];
            }
            return obj;
        });

        let insertResult = await this.storage!.insert(command.collection!, insertObjects);
        let result: DataProcessResult = { resultIDs: {} };
        Object.keys(insertResult.insertedIds).forEach(key => {
            result.resultIDs[key] = insertResult.insertedIds[key].toString();
        });
        return result;
    }

    /**
     * 获取数量
     * @param {string} commandID 命令ID
     * @param {{}} paramValues 参数
     */
    async count?(commandID: string, paramValues: {}): Promise<number> {
        // 检查有效性并获取命令
        const { command, filterName } = this.getCommandAndFilterName(commandID);
        // 获取条件
        let where = command.getCondition(filterName, paramValues);

        return await this.storage!.count(command.collection!, where);
    }
    /**
     * 更新     
     * @param commandID 命令ID
     * @param objects 待更新对象列表
     * 
     */
    async update?(commandID: string, objects: Object[]): Promise<number> {
        // 检查有效性并获取命令
        const { command } = this.getCommandAndFilterName(commandID);
        const result = await this.storage!.update(command.collection, objects);
        return result;
    }
    /**
     * 删除数据
     * @param {string} commandID 命令ID
     * @param {Object[]} objects 插入对象集合
     */
    async delete?(commandID: string, objects: Object[]): Promise<number> {
        // 检查有效性并获取命令
        const { command, primaryKeys } = this.getCommandAndFilterName(commandID);
        let result = await this.storage!.delete(command.collection!, primaryKeys, objects);
        return result;
    }
}